// DS: 1.15.6
import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import type { PapiUser } from '@wix/da-papi-types';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import UserAvatar from '../Avatar/UserAvatar';
import UserLink from '../Link/UserLink';
import BasicUserName from '../BasicUserName';
import Timestamp, { type TimestampProps } from '../../Timestamp';

import s from './IdentityVertical.scss';

interface Props {
  user: PapiUser;
  size: 'large' | 'medium';
  lastSaleTimestamp?: TimestampProps['timestamp'];
  /** If false, don't display watcher count or last sale */
  withDetails?: boolean;
  /** If false, neither avatar or username are links */
  withLinks?: boolean;
  className?: string;
  avatarClassName?: string;
  usernameClassName?: string;
}

const IdentityVertical: React.FC<Props> = ({
  user,
  size,
  lastSaleTimestamp,
  withDetails = true,
  withLinks = true,
  className,
  avatarClassName,
  usernameClassName,
}) => {
  const { t } = useTranslation();
  const watchersCount = user.stats?.watchers ?? 0;

  return (
    <div className={classnames(s['root'], s[size], className)}>
      <UserAvatar
        className={classnames(s['avatar'], avatarClassName)}
        user={user}
        size={size === 'medium' ? 40 : 72}
        withLink={withLinks}
      />
      <div className={s['username-and-details']}>
        {withLinks ? (
          <UserLink className={classnames(s['username'], usernameClassName)} user={user} />
        ) : (
          <div className={classnames(s['username'], usernameClassName)}>
            <BasicUserName user={user} />
          </div>
        )}
        {withDetails && (
          <div className={s['details']}>
            <span className={s['detail']}>
              {t('avatar.watchers', {
                formattedCount: shortenNumber(watchersCount),
                count: watchersCount,
              })}
            </span>
            {lastSaleTimestamp && (
              <>
                <div role="presentation" className={s['divider']} />
                <Timestamp className={s['detail']} timestamp={lastSaleTimestamp} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

IdentityVertical.displayName = 'IdentityVertical';

export default IdentityVertical;
